import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE, auditLog } from "@/lib/api";
import { GATEWAY_URL, isStudioEdition } from "@/lib/config";
import { saveIssuedTokens } from "@/lib/tokenStore";
import { getOrCreateDeviceId } from "@/lib/deviceFingerprint";

export function ActivationPage() {
  const [searchParams] = useSearchParams();
  const [licenseKey, setLicenseKey] = useState(searchParams.get("key") || searchParams.get("license_key") || "");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const keyParam = searchParams.get("key") || searchParams.get("license_key");
    const emailParam = searchParams.get("email");
    if (keyParam) setLicenseKey(keyParam);
    if (emailParam) setEmail(emailParam);
  }, [searchParams]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!licenseKey.trim()) return;

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address matching your purchase.");
      return;
    }

    setError(null);
    setLoading(true);
    await auditLog("FRONTEND_LICENSE_ACTIVATION_SUBMITTED");

    try {
      if (isStudioEdition()) {
        // Studio mode: coordinate through local Python server
        const res = await fetch(`${API_BASE()}/api/activate_local`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            license_key: licenseKey.trim(),
            email: email.trim(),
          }),
        });

        const data = await res.json();
        if (res.ok) {
          if (data.boot_jwt) {
            saveIssuedTokens(data.boot_jwt, licenseKey.trim(), data.device_id || "");
          }
          setSuccess(true);
          await auditLog("FRONTEND_LICENSE_ACTIVATION_SUCCESS");
          setTimeout(() => {
            navigate("/");
          }, 1500);
        } else {
          setError(data.detail || data.message || "Failed to activate license.");
          await auditLog("FRONTEND_LICENSE_ACTIVATION_FAILED", { error: data.detail });
        }
      } else {
        // Lite mode: direct to central platform-core gateway
        const deviceId = getOrCreateDeviceId();
        const deviceName = typeof window !== "undefined" && window.navigator?.userAgent
          ? (window.navigator.userAgent.includes("Windows") ? "Windows Device" : window.navigator.userAgent.includes("Mac") ? "macOS Device" : "Web Client")
          : "Web Client";

        // 1. Attempt license activation / binding to this device
        let activateOk = false;
        const actRes = await fetch(`${GATEWAY_URL}/api/v1/faceshift/activate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            license_key: licenseKey.trim(),
            email: email.trim(),
            device_id: deviceId,
            device_name: deviceName,
          }),
        });
        const actData = await actRes.json().catch(() => ({}));

        if (actRes.ok && actData.valid) {
          activateOk = true;
        } else if (actRes.status === 409) {
          // Already bound to this or another authorized device — proceed to issue-boot
          activateOk = true;
        } else {
          setError(actData.error || actData.message || "Invalid or unassigned license key.");
          await auditLog("FRONTEND_LICENSE_ACTIVATION_FAILED", { error: actData.error });
          return;
        }

        if (activateOk) {
          // 2. Request 7-day boot JWT token from central licensing server
          const bootRes = await fetch(`${GATEWAY_URL}/api/v1/streampersona/auth/issue-boot`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              license_key: licenseKey.trim(),
              email: email.trim(),
              device_id: deviceId,
            }),
          });
          const bootData = await bootRes.json().catch(() => ({}));

          if (bootRes.ok && bootData.token) {
            saveIssuedTokens(bootData.token, licenseKey.trim(), deviceId);
            setSuccess(true);
            await auditLog("FRONTEND_LICENSE_ACTIVATION_SUCCESS");
            setTimeout(() => {
              navigate("/");
            }, 1500);
          } else {
            const errMsg = bootData.error || "License key is already assigned to a different computer.";
            setError(errMsg);
            await auditLog("FRONTEND_LICENSE_ACTIVATION_FAILED", { error: errMsg });
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Network error: ${msg}`);
      await auditLog("FRONTEND_LICENSE_ACTIVATION_ERROR", { error: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
        background: "radial-gradient(circle at center, #16181c 0%, #0e0e10 100%)",
        fontFamily: "var(--font-sans)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Abstract Glowing Aura behind card */}
      <div
        style={{
          position: "absolute",
          width: 350,
          height: 350,
          background: "var(--accent)",
          opacity: 0.04,
          filter: "blur(80px)",
          borderRadius: "50%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          width: 400,
          padding: "var(--space-8) var(--space-6)",
          background: "rgba(22, 22, 24, 0.65)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "var(--radius-xl)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header/Brand Logo */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32 }}>
          <svg
            width="44"
            height="44"
            viewBox="0 0 32 32"
            style={{
              marginBottom: 16,
              filter: "drop-shadow(0 0 16px rgba(43, 181, 168, 0.35))",
            }}
          >
            <rect width="32" height="32" rx="8" fill="#161922" stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1" />
            <path
              d="M13.5 8C9.35786 8 6 11.5817 6 16C6 20.4183 9.35786 24 13.5 24"
              stroke="#2BB5A8"
              strokeWidth="2.75"
              strokeLinecap="round"
            />
            <path
              d="M18.5 8C22.6421 8 26 11.5817 26 16C26 20.4183 22.6421 24 18.5 24"
              stroke="#E76F51"
              strokeWidth="2.75"
              strokeLinecap="round"
            />
            <circle cx="16" cy="16" r="1.75" fill="#F0F2EE" />
          </svg>
          <h2
            style={{
              fontSize: "var(--text-xl)",
              fontWeight: "var(--weight-semibold)",
              color: "var(--text-primary)",
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            Activate StreamPersona
          </h2>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-secondary)",
              textAlign: "center",
              lineHeight: "var(--leading-normal)",
              padding: "0 var(--space-2)",
            }}
          >
            Enter your license key to unlock real-time face swap & voice cloning engines.
          </p>
        </div>

        {success ? (
          <div style={{ textAlign: "center", padding: "var(--space-4) 0" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--status-live-dim)",
                border: "1px solid var(--status-live)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                color: "var(--status-live)",
                margin: "0 auto 16px",
              }}
            >
              ✓
            </div>
            <div
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--weight-medium)",
                color: "var(--text-primary)",
                marginBottom: 8,
              }}
            >
              License Activated!
            </div>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
              Redirecting you to the dashboard...
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "var(--space-4)" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--weight-semibold)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--text-secondary)",
                  marginBottom: "var(--space-2)",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                disabled={loading}
                placeholder="name@domain.com"
                style={{
                  width: "100%",
                  height: 42,
                  padding: "0 var(--space-4)",
                  fontSize: "var(--text-base)",
                  color: "var(--text-primary)",
                  background: "rgba(0, 0, 0, 0.2)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "var(--radius-md)",
                  outline: "none",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                  transition: "border-color var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--accent)";
                  e.target.style.boxShadow = "0 0 8px var(--accent-glow)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.boxShadow = "inset 0 2px 4px rgba(0,0,0,0.2)";
                }}
              />
            </div>

            <div style={{ marginBottom: "var(--space-6)" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--weight-semibold)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--text-secondary)",
                  marginBottom: "var(--space-2)",
                }}
              >
                License Key
              </label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                disabled={loading}
                placeholder="SP-XXXX-XXXX-XXXX"
                style={{
                  width: "100%",
                  height: 42,
                  padding: "0 var(--space-4)",
                  fontSize: "var(--text-base)",
                  fontFamily: "var(--font-mono)",
                  color: "var(--text-primary)",
                  background: "rgba(0, 0, 0, 0.2)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "var(--radius-md)",
                  outline: "none",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                  transition: "border-color var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "var(--accent)";
                  e.target.style.boxShadow = "0 0 8px var(--accent-glow)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.boxShadow = "inset 0 2px 4px rgba(0,0,0,0.2)";
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--status-error)",
                  background: "var(--status-error-dim)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  borderRadius: "var(--radius-md)",
                  padding: "var(--space-3)",
                  marginBottom: "var(--space-5)",
                  lineHeight: "var(--leading-tight)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !licenseKey.trim() || !email.trim()}
              style={{
                width: "100%",
                height: 42,
                fontSize: "var(--text-base)",
                fontWeight: "var(--weight-semibold)",
                fontFamily: "var(--font-sans)",
                color: "#000",
                background: loading || !licenseKey.trim() || !email.trim() ? "var(--bg-floating)" : "var(--accent)",
                border: "none",
                borderRadius: "var(--radius-md)",
                cursor: loading || !licenseKey.trim() || !email.trim() ? "default" : "pointer",
                transition: "background var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out)",
                boxShadow: loading || !licenseKey.trim() || !email.trim() ? "none" : "0 4px 12px var(--accent-glow)",
              }}
              onMouseEnter={(e) => {
                if (!loading && licenseKey.trim() && email.trim()) {
                  e.currentTarget.style.background = "var(--accent-hover)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 212, 170, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && licenseKey.trim() && email.trim()) {
                  e.currentTarget.style.background = "var(--accent)";
                  e.currentTarget.style.boxShadow = "0 4px 12px var(--accent-glow)";
                }
              }}
            >
              {loading ? "Activating..." : "Activate Software"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
